import React, { useMemo, useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import StepWelcome from "../components/Onboarding/StepWelcome";
import StepNeeds from "../components/Onboarding/StepNeeds";
import StepSummary from "../components/Onboarding/StepSummary";
import StepComplete from "../components/Onboarding/StepComplete";
import StepRegion from "../components/Onboarding/StepRegion";
import StepGoals from "../components/Onboarding/StepGoals";
import {
  setNeedsSelection,
  setRegions,
  setMarkets,
  setDescription,
  setSummary,
  completeOnboarding,
  OnboardingSummary,
} from "../store/onboardingSlice";
import { postPersona, setPreferredPersona, fetchPersonas, summarizePersona } from "../store/personaSlice";
import { markStepComplete } from "../store/clubChecklistSlice";
import { RootState, AppDispatch } from "../store";
import { getMeUser } from "../store/userSlice";
import { usePostHogCapture } from "../hooks/usePostHogCapture";
import { POSTHOG_EVENTS } from "../utils/posthogEvents";
import { setModalState, modalViewsConst } from "../store/modalSlice";

// Define the possible steps in the onboarding process
type Step = "welcome" | "needs" | "region" | "goals" | "summary" | "complete";

// Preset summaries for different needs
type SummaryPreset = {
  role: string;
  market: string;
  focus: string;
  recommendations: readonly string[];
};

// Mapping of needs to summary presets. These are used to auto-generate summaries based on user selections.
const SUMMARY_PRESETS: Record<string, SummaryPreset> = {
  "Day-Ahead Trading": {
    role: "Day-Ahead Trader",
    market: "ERCOT",
    focus: "Wind Generation",
    recommendations: [
      "Next-day wind forecasts",
      "Comparison to ISO forecasts",
      "Real-time accuracy metrics",
    ],
  },
  "Hydro Operations": {
    role: "Hydro Operator",
    market: "PJM",
    focus: "Reservoir Optimization",
    recommendations: [
      "Reservoir inflow outlook",
      "Generation deviation alerts",
      "Multi-site balancing insights",
    ],
  },
  "Wind Forecasting": {
    role: "Renewable Analyst",
    market: "SPP",
    focus: "Wind Generation",
    recommendations: [
      "Turbine-level performance",
      "Ramp risk notifications",
      "Real-time accuracy metrics",
    ],
  },
  "Market Analysis": {
    role: "Market Analyst",
    market: "CAISO",
    focus: "Price Strategy",
    recommendations: [
      "Next-day congestion view",
      "ISO comparison reports",
      "Trade-ready briefs",
    ],
  },
  "Asset Management": {
    role: "Asset Manager",
    market: "NYISO",
    focus: "Fleet Optimization",
    recommendations: [
      "Asset health insights",
      "Production vs forecast variance",
      "Investment-grade exports",
    ],
  },
  "Real-Time Ops": {
    role: "Real-Time Operator",
    market: "ERCOT",
    focus: "Grid Stability",
    recommendations: [
      "Minute-by-minute ramp alerts",
      "Deviation dashboards",
      "Control room quick actions",
    ],
  },
} as const;

// Default summary if no specific presets match
const DEFAULT_SUMMARY: SummaryPreset = {
  role: "Energy Professional",
  market: "ERCOT",
  focus: "Renewable Operations",
  recommendations: [
    "Next-day wind forecasts",
    "Comparison to ISO forecasts",
    "Real-time accuracy metrics",
  ],
};

// Function to clone a summary preset
const cloneSummary = (preset: SummaryPreset) => ({
  role: preset.role,
  market: preset.market,
  focus: preset.focus,
  recommendations: [...preset.recommendations],
});

// Function to derive a summary based on user needs, description, and markets
function deriveSummary(
  needs: string[],
  description: string,
  markets: string[]
): OnboardingSummary {
  const primaryNeed = needs[0];
  if (primaryNeed && primaryNeed in SUMMARY_PRESETS) {
    const preset = SUMMARY_PRESETS[primaryNeed as keyof typeof SUMMARY_PRESETS];
    if (preset) {
      const summary = cloneSummary(preset);
      if (markets.length > 0) {
        summary.market = markets[0];
      }
      return summary;
    }
  }
  if (description?.toLowerCase().includes("hydro")) {
    return cloneSummary(SUMMARY_PRESETS["Hydro Operations"]);
  }
  if (description?.toLowerCase().includes("wind")) {
    return cloneSummary(SUMMARY_PRESETS["Wind Forecasting"]);
  }
  const base = cloneSummary(DEFAULT_SUMMARY);
  if (markets.length > 0) {
    base.market = markets[0];
  }
  return base;
}

// Main OnboardingV2 component
function OnboardingV2() {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { capture } = usePostHogCapture();
  const onboarding = useSelector((state: RootState) => state.onboarding);
  const userId = useSelector((state: RootState) => (state.user.permissionDetails as { id?: string })?.id);
  const { personas } = useSelector((state: RootState) => state.personas);
  const [step, setStep] = useState<Step>("welcome");

  // Track step changes
  const handleStepChange = (newStep: Step, fromStep: Step) => {
    capture(POSTHOG_EVENTS.ONBOARDING_STEP_COMPLETED, {
      step_completed: fromStep,
      next_step: newStep,
    });
    setStep(newStep);
  };

  // Fetch personas on mount
  useEffect(() => {
    dispatch(fetchPersonas());
  }, [dispatch]);

  // Redirect to home if onboarding is already completed
  useEffect(() => {
    if (onboarding.completed) {
      navigate("/home", { replace: true });
    }
  }, [onboarding.completed, navigate]);

  // Memoized summary derived from onboarding state
  const summary = useMemo<OnboardingSummary>(
    () =>
      onboarding.summary ??
      deriveSummary(
        onboarding.selectedNeeds,
        onboarding.description,
        onboarding.markets
      ),
    [
      onboarding.summary,
      onboarding.selectedNeeds,
      onboarding.description,
      onboarding.markets,
    ]
  );

  // Function to proceed to the summary step
  const goToSummary = () => {
    dispatch(setSummary(summary));
    handleStepChange("summary", "goals");
  };

  // Function to handle confirmation of the summary
  const handleConfirm = () => {
    handleStepChange("complete", "summary");
  };

  const [isSummarizing, setIsSummarizing] = useState(false);
  const [summarizeError, setSummarizeError] = useState(false);

  // Function to handle finishing the onboarding
  const handleFinish = async () => {
    if (summarizeError) {
      dispatch(completeOnboarding());
      navigate("/home", { replace: true });
      return;
    }
    setSummarizeError(false);
    setIsSummarizing(true);
    try {
      const personaInput = {
        userId: userId,
        name: summary.role || "My Persona",
        selectedNeeds: onboarding.selectedNeeds,
        regions: onboarding.regions,
        markets: onboarding.markets,
        description: onboarding.description,
        summary: JSON.stringify(summary),
        completed: true,
      };
      const result = await dispatch((postPersona as any)(personaInput));

      if (result?.payload?.id) {
        dispatch(markStepComplete(2));
      }

      // Set the new persona as the preferred persona
      if (result?.payload?.id && userId) {
        await dispatch(
          setPreferredPersona({ userId, personaId: result.payload.id })
        );
        // Refresh user data to get updated preferredPersonaId
        await dispatch((getMeUser as any)(userId));

        // Generate AI persona summary and show results modal
        const summaryResult = await dispatch(
          summarizePersona({
            personaId: result.payload.id,
            selectedNeeds: onboarding.selectedNeeds,
            regions: onboarding.regions,
            markets: onboarding.markets,
            description: onboarding.description,
          })
        );
        setIsSummarizing(false);

        if (summaryResult?.payload) {
          capture(POSTHOG_EVENTS.PERSONA_CREATED, {
            persona_name: summary.role,
            selected_needs: onboarding.selectedNeeds,
            regions: onboarding.regions,
            markets: onboarding.markets,
          });
          dispatch(
            setModalState({
              modalOpen: true,
              modalView: modalViewsConst.personaReady,
              properties: { ...summaryResult.payload, __modalSize: "sm" },
            })
          );
          return;
        }

        // summarizePersona failed — show error and let user continue
        setSummarizeError(true);
        return;
      }

      // Track persona creation
      capture(POSTHOG_EVENTS.PERSONA_CREATED, {
        persona_name: summary.role,
        selected_needs: onboarding.selectedNeeds,
        regions: onboarding.regions,
        markets: onboarding.markets,
      });
    } catch (error) {
      console.log("Error saving persona:", error);
      setIsSummarizing(false);
    }
    dispatch(completeOnboarding());
    navigate("/home", { replace: true });
  };

  // Render different steps based on the current step state
  if (step === "needs") {
    return (
      <StepNeeds
        selected={onboarding.selectedNeeds}
        onSelect={(value) => dispatch(setNeedsSelection(value))}
        onBack={() => setStep("welcome")}
        onContinue={() => handleStepChange("region", "needs")}
        hasExistingPersonas={Object.keys(personas).length > 0}
        onExit={() => navigate("/home")}
      />
    );
  }

  // Render the region selection step
  if (step === "region") {
    return (
      <StepRegion
        regions={onboarding.regions}
        markets={onboarding.markets}
        onRegionsChange={(value) => dispatch(setRegions(value))}
        onMarketsChange={(value) => dispatch(setMarkets(value))}
        onBack={() => setStep("needs")}
        onContinue={() => handleStepChange("goals", "region")}
        hasExistingPersonas={Object.keys(personas).length > 0}
        onExit={() => navigate("/home")}
      />
    );
  }

  // Render the goals step
  if (step === "goals") {
    return (
      <StepGoals
        description={onboarding.description}
        onDescriptionChange={(value: string) => dispatch(setDescription(value))}
        onBack={() => setStep("region")}
        onContinue={goToSummary}
        hasExistingPersonas={Object.keys(personas).length > 0}
        onExit={() => navigate("/home")}
      />
    );
  }

  // Render the summary step
  if (step === "summary") {
    return (
      <StepSummary
        role={summary.role}
        focus={summary.focus}
        recommendations={summary.recommendations}
        selectedNeeds={onboarding.selectedNeeds}
        regions={onboarding.regions}
        markets={onboarding.markets}
        description={onboarding.description}
        hasExistingPersonas={Object.keys(personas).length > 0}
        onClarify={() => setStep("goals")}
        onConfirm={handleConfirm}
        onBack={() => setStep("goals")}
        onExit={() => navigate("/home")}
      />
    );
  }

  // Render the completion step
  if (step === "complete") {
    return (
      <StepComplete
        onFinish={handleFinish}
        isLoading={isSummarizing}
        hasError={summarizeError}
        hasExistingPersonas={Object.keys(personas).length > 0}
        onExit={() => navigate("/home")}
      />
    );
  }
  // Default to the welcome step
  return (
    <StepWelcome
      onContinue={() => handleStepChange("needs", "welcome")}
      hasExistingPersonas={Object.keys(personas).length > 0}
      onExit={() => navigate("/home")}
    />
  );
}

 export default OnboardingV2;
